import { Button } from 'components-antd';
import { Plus, SearchIcon } from 'components/Icons';
import KitsTable from './components/KitsTable';
import styles from './styles.module.scss';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button as Button2, PageNotFound, Spinner } from 'components';
import { routes } from 'settings/navigation/routes';
import { getAllKits, getKitsCategories, getKitsFrequencies, getKitsPropertyTypes } from 'api/kits';
import { getAllTeams } from 'api/teamList';
import PublishModal from './components/PublishModal';
import CreationModal from './components/CreationModal';
import EditModal from './components/EditModal';
import useReminderTemplate from './hooks/use-reminder-template.hook';
import RemindedModal from './components/ReminderModal';
import { Input } from 'components';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import { keyBy } from 'lodash-es';

function Kits() {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [kits, setKits] = useState<any[]>([]);
  const [filteredKits, setFilteredKits] = useState<any[] | null>(null);
  const [kitsCategories, setKitsCategories] = useState<any[]>([]);
  const [kitsFrequencies, setKitsFrequencies] = useState<any[]>([]);
  const [kitsPropertyTypes, setKitsPropertyTypes] = useState<any[]>([]);
  const [agents, setAgent] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [targetKit, setTargetKit] = useState<any>(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState<boolean>(false);
  const [showCreationModal, setShowCreationModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showReminderModal, setShowReminderModal] = useState<boolean>(false);
  const [cloneId, setCloneId] = useState<number | null>(null);
  const history = useHistory();

  const { loadReminderTemplates } = useReminderTemplate();

  const fetchKitsData = async () => {
    try {
      const kitsResponse = await getAllKits();
      if (kitsResponse.status === 200) {
        setKits(kitsResponse?.data?.result?.length ? kitsResponse?.data?.result : []);
      } else {
        setError(true);
      }
      const kitsCategoriesResponse = await getKitsCategories();
      if (kitsCategoriesResponse.status === 200) {
        setKitsCategories(
          kitsCategoriesResponse?.data?.result?.length ? kitsCategoriesResponse?.data?.result : [],
        );
      } else {
        setError(true);
      }
      const kitsFrequenciesResponse = await getKitsFrequencies();
      if (kitsFrequenciesResponse.status === 200) {
        setKitsFrequencies(
          kitsFrequenciesResponse?.data?.result?.length
            ? kitsFrequenciesResponse?.data?.result
            : [],
        );
      } else {
        setError(true);
      }
      const kitsPropertyTypesResponse = await getKitsPropertyTypes();
      if (kitsPropertyTypesResponse.status === 200) {
        setKitsPropertyTypes(
          kitsPropertyTypesResponse?.data?.result?.length
            ? kitsPropertyTypesResponse?.data?.result
            : [],
        );
      } else {
        setError(true);
      }
      const teamsResponse = await getAllTeams();
      if (teamsResponse.status === 200) {
        setTeams(teamsResponse?.data?.result?.length ? teamsResponse?.data?.result : []);
      } else {
        setError(true);
      }
      setLoading(false);
    } catch (e) {
      setError(true);
      setLoading(false);
    }
  };

  const setPermissionsHandler = async () => {
    const kitsResponse = await getAllKits();
    if (kitsResponse.status === 200 && kitsResponse?.data?.result?.length) {
      setKits(kitsResponse?.data?.result);
    } else {
      setError(true);
    }
  };

  useEffect(() => {
    async function fetchData() {
      fetchKitsData();
      loadReminderTemplates({ entityName: 'Kit' });
    }
    if (loading) void fetchData();
  }, [loading]);

  useEffect(() => {
    if (showCreationModal === false) {
      setCloneId(null);
    }
  }, [showCreationModal]);

  const permitClickHandler = (id: number) => {
    if (id && kits?.length) {
      const kit = kits.find((obj) => obj.Id === id);
      if (kit) {
        setTargetKit(kit);
        setShowPermissionsModal(true);
      }
    }
  };

  const editKitClickHandler = (id: number) => {
    if (id && kits?.length) {
      const kit = kits.find((obj) => obj.Id === id);
      if (kit) {
        setTargetKit(kit);
        setShowEditModal(true);
      }
    }
  };

  const reminderKitClickHandler = (id: number) => {
    if (id && kits?.length) {
      const kit = kits.find((obj) => obj.Id === id);
      if (kit) {
        setTargetKit(kit);
        setShowReminderModal(true);
      }
    }
  };

  const templateClickHandler = (id: number) => {
    if (id) {
      history.push(`/dashboard/kits/template/${id}`);
    }
  };

  const updateNotesHandler = (kitId: number, notes: string) => {
    if (kitId && notes) {
      const kitsCopy = [...kits];
      const targetKitIndex = kitsCopy.findIndex((kit) => kit.Id === kitId);
      if (targetKitIndex !== -1) {
        const targetKit = { ...kitsCopy[targetKitIndex], Notes: notes };
        kitsCopy[targetKitIndex] = targetKit;
        setKits(kitsCopy);
      }
    }
    setShowEditModal(false);
  };

  const updateKitHandler = (savedkit) => {
    if (savedkit) {
      const kitsCopy = [...kits];
      const targetKitIndex = kitsCopy.findIndex((kit) => kit.Id === savedkit.Id);
      if (targetKitIndex !== -1) {
        const targetKit = { ...kitsCopy[targetKitIndex], ...savedkit };
        kitsCopy[targetKitIndex] = targetKit;
        setKits(kitsCopy);
      }
    }
    setShowEditModal(false);
  };

  const newKitHandler = (newKit) => {
    if (newKit) {
      const kitsCopy = [...kits, newKit];
      setKits(kitsCopy);
    }
    setShowCreationModal(false);
  };

  const handleSubmitSearch = useCallback(
    (values) => {
      const searchString = values.searchString.toLowerCase();
      if (!searchString) {
        setFilteredKits(null);
        return;
      }
      const filteredKits = kits.filter(
        (kit) =>
          kit.Name?.toLowerCase().includes(searchString) ||
          kit.InternalName?.toLowerCase()?.includes(searchString),
      );
      setFilteredKits(filteredKits);
    },
    [kits],
  );

  const formik = useFormik({
    initialValues: {
      searchString: '',
    },
    onSubmit: handleSubmitSearch,
  });

  const kitsMap = useMemo(() => {
    return keyBy(kits, 'Id');
  }, [kits]);

  const cloneKit = useMemo(() => {
    if (cloneId && kitsMap[cloneId]) {
      return kitsMap[cloneId];
    }
    return null;
  }, [cloneId, kitsMap]);

  const setCloneIdCB = useCallback((id: number) => {
    setCloneId(id);
    setShowCreationModal(true);
  }, []);

  if (loading) {
    return <Spinner />;
  } else if (error) {
    return (
      <div>
        <PageNotFound backUrl={routes.index} />
      </div>
    );
  } else
    return (
      <div className={styles.adminKits}>
        {showPermissionsModal ? (
          <PublishModal
            showModal={setShowPermissionsModal}
            open={showPermissionsModal}
            agents={agents}
            teams={teams}
            targetKit={targetKit}
            setPermissionsHandler={setPermissionsHandler}
            updateNotesHandler={updateNotesHandler}
          />
        ) : (
          <></>
        )}
        {showCreationModal ? (
          <CreationModal
            showModal={setShowCreationModal}
            open={showCreationModal}
            categories={kitsCategories}
            frequencies={kitsFrequencies}
            propertyTypes={kitsPropertyTypes}
            newKitHandler={newKitHandler}
            cloneKit={cloneKit}
          />
        ) : (
          <></>
        )}
        {showEditModal ? (
          <CreationModal
            showModal={setShowEditModal}
            open={showEditModal}
            categories={kitsCategories}
            frequencies={kitsFrequencies}
            propertyTypes={kitsPropertyTypes}
            newKitHandler={updateKitHandler}
            cloneKit={targetKit}
            isUpdate={true}
            setCloneId={setCloneIdCB}
          />
        ) : (
          <></>
        )}
        {showReminderModal ? (
          <RemindedModal
            showModal={setShowReminderModal}
            open={showReminderModal}
            targetKit={targetKit}
          />
        ) : (
          <></>
        )}
        <div className={styles.adminKitsHeader}>
          <div className={styles.empty}></div>
          <h1 className={styles.heading}>KITs</h1>
          <Button className={styles.newKit} onClick={() => setShowCreationModal(true)}>
            <Plus className={styles.iconAdd} />
            New
          </Button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
          className={styles.filtersWrapper}
        >
          <Input
            id="searchString"
            className={styles.searchInput}
            type="text"
            name="searchString"
            placeholder="Search"
            error={formik.touched.searchString ? formik.errors.searchString : ''}
            value={formik.values.searchString}
            onChange={formik.handleChange}
            disabled={loading}
            variant={Input.LIGHT_FULL}
            data-testid="search_input"
          />
          <Button2
            type="submit"
            className={styles.searchButton}
            isPending={loading}
            title={<SearchIcon />}
            testid="search_button"
          />
        </form>
        <div className={styles.adminKitsContent}>
          <KitsTable
            kitsData={filteredKits || kits}
            categoriesData={kitsCategories}
            frequenciesData={kitsFrequencies}
            propertyTypesData={kitsPropertyTypes}
            permitClickHandler={permitClickHandler}
            editKitClickHandler={editKitClickHandler}
            reminderTemplateClickHandler={reminderKitClickHandler}
            templateClickHandler={templateClickHandler}
          />
        </div>
      </div>
    );
}

export default Kits;
